import { useState, useEffect, useCallback } from "react";
import { setPageDetail } from "@/hooks/usePageTitle";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { NewsTab } from "@/components/profile/ProfileExtras";
import { FileDown, Crown, Lock } from "lucide-react";
import { NominateCard } from "@/components/NominateCard";
import useScrollRestoration from "@/hooks/useScrollRestoration";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { OverviewTab } from "@/components/profile/OverviewTab";
import { VotingTab } from "@/components/profile/VotingTab";
import { CommitteesTab } from "@/components/profile/CommitteesTab";
import { WatchButton } from "@/components/WatchButton";
import { LegislationTab } from "@/components/profile/LegislationTab";
import { ordinal, statusPillClass } from "@/components/profile/shared";
import { ArrowLeft, TrendingUp, TrendingDown, Award, Vote, Users as UsersIcon, CheckCircle, XCircle, MinusCircle, Calendar, Zap, Shuffle, Network, GraduationCap } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, ReferenceLine } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegislatorProfile = () => {
  const { legislatorId } = useParams();
  const navigate = useNavigate();
  const [legislator, setLegislator] = useState(null);
  const [recentVotes, setRecentVotes] = useState([]);
  const [sponsoredBills, setSponsoredBills] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [committees, setCommittees] = useState([]);
  const [surpriseVotes, setSurpriseVotes] = useState([]);
  const [votingTribe, setVotingTribe] = useState(null);
  const [flipFlops, setFlipFlops] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(0);
  const [statScope, setStatScope] = useState("current");
  const [briefBusy, setBriefBusy] = useState(false);
  const { featureMode } = useAuth();
  const briefLocked = featureMode("legislator_brief") === "none";
  const generateBrief = async () => {
    setBriefBusy(true);
    try {
      let res = await axios.get(`${API}/legislators/${legislatorId}/brief`, { responseType: "blob", timeout: 120000, validateStatus: (st) => st === 200 || st === 202 });
      for (let i = 0; i < 20 && res.status === 202; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        res = await axios.get(`${API}/legislators/${legislatorId}/brief`, { responseType: "blob", timeout: 120000, validateStatus: (st) => st === 200 || st === 202 });
      }
      if (res.status !== 200) throw new Error("timeout");
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `WHIP-Brief-${(legislator?.name || legislatorId).replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Brief downloaded");
    } catch {
      toast.error("Couldn't generate the brief right now, please try again in a moment");
    } finally {
      setBriefBusy(false);
    }
  };
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [billsDialogOpen, setBillsDialogOpen] = useState(false);
  const [billsDialogData, setBillsDialogData] = useState({ bills: [], title: "" });
  const [lesScope, setLesScope] = useState("current"); // "current" | "career"
  const [lesHistory, setLesHistory] = useState([]);

  // Restore scroll position when the user navigates back here from a Bill
  // page. Marked ready once the legislator profile has finished loading so
  // the document has expanded to full height.
  const { saveScroll } = useScrollRestoration(
    `legislator:${legislatorId}`,
    !loading && legislator !== null
  );

  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const [sv, tribe, ff, lh] = await Promise.all([
        axios.get(`${API}/legislators/${legislatorId}/surprise-votes`).catch(() => ({ data: [] })),
        axios.get(`${API}/legislators/${legislatorId}/voting-tribe`).catch(() => ({ data: null })),
        axios.get(`${API}/legislators/${legislatorId}/flip-flops`).catch(() => ({ data: null })),
        axios.get(`${API}/legislators/${legislatorId}/les-history`).catch(() => ({ data: [] })),
      ]);
      setSurpriseVotes(sv.data || []);
      setVotingTribe(tribe.data || null);
      setFlipFlops(ff.data && Array.isArray(ff.data.topics) ? ff.data : null);
      setLesHistory(Array.isArray(lh.data) ? lh.data : []);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [legislatorId]);

  const fetchLegislatorData = useCallback(async () => {
    const RETRY_DELAYS = [2500, 5000, 8000, 12000];
    const fetchWithRetry = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await Promise.all([
            axios.get(`${API}/legislators/${legislatorId}`),
            // Voting history is Intelligence-tier; a 403 just means "locked", not an outage.
            axios.get(`${API}/legislators/${legislatorId}/votes`).catch((e) => (e?.response?.status === 403 ? { data: [] } : Promise.reject(e))),
            axios.get(`${API}/legislators/${legislatorId}/sponsored-bills`, { params: { limit: 250 } }),
          ]);
        } catch (err) {
          // A 404 is a real miss; anything else (520/502/timeout) is the
          // backend blipping, wait and retry instead of failing the page.
          if (err?.response?.status === 404 || attempt >= RETRY_DELAYS.length) throw err;
          setRetrying(attempt + 1);
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
        }
      }
    };
    try {
      setLoading(true);
      setRetrying(0);
      setLesScope("current");
      const [legRes, votesRes, billsRes] = await fetchWithRetry();
      setLegislator(legRes.data);
      setPageDetail(legRes.data?.name);
      // Smart default LES scope: if the member has no activity yet this
      // Congress but a real career score, open on Career so the card doesn't
      // greet the user with a jarring 0.00.
      const st = legRes.data?.stats || {};
      if (!st.effectivenessScoreCurrent && st.effectivenessScore > 0) {
        setLesScope("career");
      }
      setRecentVotes(votesRes.data);
      setSponsoredBills(billsRes.data);
      setCommittees(legRes.data.committees || []);
    } catch (error) {
      toast.error(error?.response?.status === 404 ? "Legislator not found" : "Couldn't reach the server, please try again in a moment");
    } finally {
      setRetrying(0);
      setLoading(false);
    }
  }, [legislatorId]);

  useEffect(() => {
    fetchLegislatorData();
    fetchAnalytics();
  }, [fetchLegislatorData, fetchAnalytics]);

  const handlePieClick = (data, chartType) => {
    // Handle different chart sections with appropriate data
    if (data.name === "Sponsored") {
      // Show bills table for sponsored section
      setBillsDialogData({
        bills: sponsoredBills,
        title: `Bills Sponsored by ${legislator.name}`
      });
      setBillsDialogOpen(true);
      return;
    }
    
    if (data.name === "Enacted") {
      // Show enacted bills - but since we may not have all of them, create a summary
      const passedBills = sponsoredBills.filter(b => b.status === "Enacted");
      
      // If we have enacted bills, show them
      if (passedBills.length > 0) {
        setBillsDialogData({
          bills: passedBills,
          title: `Bills Enacted into Law - ${legislator.name}`
        });
        setBillsDialogOpen(true);
      } else {
        // Show explanation instead
        setDialogData({
          title: `Bills Enacted into Law`,
          value: legislator.stats.billsPassed,
          percentage: `${((legislator.stats.billsPassed / legislator.stats.billsSponsored) * 100).toFixed(1)}% success rate`,
          description: `${legislator.name} sponsored ${legislator.stats.billsPassed} bills that were directly enacted into law, out of ${legislator.stats.billsSponsored} bills sponsored during their congressional career (${((legislator.stats.billsPassed / legislator.stats.billsSponsored) * 100).toFixed(1)}% success rate). Counts direct enactments per Congress.gov records, trackers like GovTrack also credit companion bills and provisions incorporated into other laws, so their totals run higher.`,
          chartType
        });
        setDialogOpen(true);
      }
      return;
    }
    
    if (data.name === "Pending") {
      // Show pending bills
      const pendingBills = sponsoredBills.filter(b => b.status !== "Enacted");
      setBillsDialogData({
        bills: pendingBills,
        title: `Pending Legislation - ${legislator.name}`
      });
      setBillsDialogOpen(true);
      return;
    }
    
    if (data.name === "Cast" || data.name === "Missed") {
      // Show voting attendance table
      const attendanceData = [
        {
          category: "Votes Cast",
          count: legislator.stats.votesCast,
          percentage: ((legislator.stats.votesCast / legislator.stats.votesCount) * 100).toFixed(1) + "%",
          status: "Present"
        },
        {
          category: "Votes Missed",
          count: legislator.stats.votesCount - legislator.stats.votesCast,
          percentage: (((legislator.stats.votesCount - legislator.stats.votesCast) / legislator.stats.votesCount) * 100).toFixed(1) + "%",
          status: "Absent"
        },
        {
          category: "Total Votes",
          count: legislator.stats.votesCount,
          percentage: "100%",
          status: "All"
        }
      ];
      
      setBillsDialogData({
        bills: [],
        title: `Voting Attendance Record - ${legislator.name}`,
        isAttendanceData: true,
        attendanceData: attendanceData
      });
      setBillsDialogOpen(true);
      return;
    }
    
    // Default handling for other sections
    setDialogData({
      title: `${chartType}: ${data.name}`,
      value: data.value,
      percentage: data.percent ? `${(data.percent * 100).toFixed(1)}%` : null,
      description: getChartDescription(data.name, data.value, chartType),
      chartType
    });
    setDialogOpen(true);
  };

  const openBillsDrilldown = async (kind) => {
    // Fetch fresh from the backend so we can reliably show "passed" even when
    // the `sponsoredBills` in state doesn't include them.
    try {
      setBillsDialogData({
        bills: [],
        title: kind === "passed"
          ? `Bills Enacted into Law, ${legislator.name}`
          : `Bills Sponsored, ${legislator.name}`,
        loading: true,
      });
      setBillsDialogOpen(true);
      const resp = await axios.get(`${API}/legislators/${legislatorId}/bills`, { params: { kind } });
      const data = resp.data || {};
      setBillsDialogData({
        bills: data.bills || [],
        billCount: data.billCount,
        amendmentCount: data.amendmentCount,
        totalSponsored: data.totalSponsored,
        kind,
        title: kind === "passed"
          ? `Bills Enacted into Law, ${legislator.name}`
          : `Bills Sponsored, ${legislator.name}`,
        loading: false,
      });
    } catch (e) {
      toast.error("Failed to load bills");
      setBillsDialogData({ bills: [], title: "Bills", loading: false });
    }
  };
  
  const getChartDescription = (name, value, chartType) => {
    if (chartType === "Legislative Activity") {
      if (name === "Sponsored") {
        return `This legislator has sponsored ${value} pieces of legislation during their time in Congress. This includes bills and resolutions introduced to address various policy issues.`;
      } else if (name === "Enacted") {
        return `Out of all sponsored legislation, ${value} bills were directly enacted into law per Congress.gov records, a ${((value / legislator.stats.billsSponsored) * 100).toFixed(1)}% success rate. Trackers like GovTrack also credit companion bills and text-incorporated provisions, so their totals run higher.`;
      } else if (name === "Pending") {
        return `${value} pieces of legislation are currently pending or did not advance. This includes bills in committee, awaiting floor action, or that failed to pass.`;
      }
    } else if (chartType === "Attendance Record") {
      if (name === "Cast") {
        return `The legislator cast ${value} votes out of ${legislator.stats.votesCount} total votes, representing a ${((value / legislator.stats.votesCount) * 100).toFixed(1)}% attendance rate.`;
      } else if (name === "Missed") {
        return `${value} votes were missed. Reasons for missed votes can include illness, scheduling conflicts, or official congressional business.`;
      }
    }
    return "";
  };

  const getPartyColor = (party) => {
    const colors = { D: "blue", R: "red", I: "purple" };
    return colors[party] || "gray";
  };

  const getPartyName = (party) => {
    const names = { D: "Democrat", R: "Republican", I: "Independent" };
    return names[party] || party;
  };

  const getVoteIcon = (vote) => {
    if (vote === "Yea") return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (vote === "Nay") return <XCircle className="w-5 h-5 text-red-600" />;
    return <MinusCircle className="w-5 h-5 text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            {retrying > 0 ? (
              <p className="text-amber-300" data-testid="profile-retry-notice">
                Server is warming up, retrying… (attempt {retrying} of 4)
              </p>
            ) : (
              <p className="text-gray-600">Loading profile...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!legislator) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">Legislator not found</p>
            <Button onClick={() => navigate("/legislators")} className="mt-4">
              Back to Legislators
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statsData = [
    { name: "Sponsored", value: legislator.stats.billsSponsored, fill: "#3b82f6" },
    { name: "Enacted", value: legislator.stats.billsPassed, fill: "#22c55e" },
    { name: "Pending", value: legislator.stats.billsSponsored - legislator.stats.billsPassed, fill: "#f59e0b" },
  ];

  const votingData = [
    { name: "Cast", value: legislator.stats.votesCast, fill: "#22c55e" },
    { name: "Missed", value: legislator.stats.votesCount - legislator.stats.votesCast, fill: "#ef4444" },
  ];

  const partyColor = getPartyColor(legislator.party);

  // WHIP banner overlays, muted, blended into dark UI rather than campaign-style.
  const getPartyBannerClass = () => {
    if (legislator.party === 'D') return 'whip-banner-dem';
    if (legislator.party === 'R') return 'whip-banner-rep';
    if (legislator.party === 'I') return 'whip-banner-ind';
    return '';
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      {/* Hero Section, muted party gradient blended into the dark shell */}
      <div className={`${getPartyBannerClass()} text-white border-b border-white/5`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate("/legislators")}
            className="mb-4 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profiles
          </Button>

          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="relative">
              <img
                src={legislator.photo}
                alt={legislator.name}
                className="w-40 h-40 rounded-2xl object-cover border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                data-testid="legislator-photo"
              />
              <div className="absolute -bottom-3 -right-3 w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                   style={{ background: "var(--whip-surface)", border: "1px solid var(--whip-border-strong)" }}>
                <span className="text-3xl font-black text-white">{legislator.party}</span>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold mb-2 flex items-center gap-3 flex-wrap" data-testid="legislator-name">
                {legislator.name}
                <WatchButton type="legislator" refId={legislatorId} label={legislator.name} light />
              </h1>
              <p className="text-xl md:text-2xl mb-4 opacity-90" data-testid="legislator-title-line">
                {legislator.chamber === "Senate"
                  ? `${legislator.senatorRank ? `${legislator.senatorRank} ` : ""}Senator from ${legislator.state}`
                  : `Representative from ${legislator.state}${
                      legislator.district
                        ? `'s ${ordinal(String(legislator.district).split("-").pop())} District`
                        : ""
                    }`}
              </p>
              {legislator.chamber !== "Senate" && legislator.districtAreas && (
                <p className="text-sm md:text-base mb-3 -mt-2 opacity-75" data-testid="legislator-district-areas">
                  Representing {legislator.districtAreas}
                </p>
              )}
              <div className="flex flex-wrap gap-3 mb-4">
                <Badge className="bg-white/20 text-white border-white/30 border px-4 py-2 text-base">
                  {getPartyName(legislator.party)}
                </Badge>
                <Badge className="bg-white/20 text-white border-white/30 border px-4 py-2 text-base">
                  {legislator.state} • {legislator.chamber}
                </Badge>
                <Badge className="bg-white/20 text-white border-white/30 border px-4 py-2 text-base">
                  {legislator.yearsInOffice} {legislator.yearsInOffice === 1 ? "Year" : "Years"} in Office
                </Badge>
                {legislator.leadershipRole && (
                  <Badge className="bg-orange-500/25 text-white border border-orange-300/60 px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="header-leadership-badge"
                    title={`${legislator.leadershipCaucus === "majority" ? "Majority" : "Minority"} leadership in the ${legislator.chamber}.`}>
                    <Crown className="w-4 h-4 text-orange-200" /> {legislator.leadershipRole}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Key Stats Bar */}
          <div className="flex items-center justify-end gap-3 mt-8 mb-3 flex-wrap" data-testid="stat-scope-toggle">
            <div className="inline-flex rounded-lg border border-white/25 bg-white/10 p-0.5 text-xs font-semibold">
              {[["current", "119th Congress"], ["career", "Career"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setStatScope(id)} data-testid={`stat-scope-${id}`}
                  className={`px-3 py-1 rounded-md transition-colors ${statScope === id ? "bg-white text-gray-900" : "text-white/80 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
            <button type="button" onClick={briefLocked ? () => toast("Generate Brief is a Pro feature", { description: "Upgrade to download one-page PDF briefs on any legislator." }) : generateBrief} disabled={briefBusy} data-testid="generate-brief-btn"
              title={briefLocked ? "Pro feature: one-page PDF brief synthesizing this profile. Upgrade to download." : "Download a one-page PDF brief synthesizing this profile."}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/25 disabled:opacity-60 transition-colors">
              {briefLocked ? <Lock className="w-3.5 h-3.5 text-orange-200" data-testid="generate-brief-lock" /> : <FileDown className="w-3.5 h-3.5" />} {briefBusy ? "Generating brief…" : "Generate Brief"}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {(() => {
              const st = legislator.stats || {};
              const cur = statScope === "current";
              const sponsored = cur ? (st.billsSponsoredSubstantiveCurrent ?? st.billsSponsored) : st.billsSponsored;
              const enacted = cur ? (st.billsLawCurrent ?? st.billsPassed) : st.billsPassed;
              const votePct = st.votesCount ? ((st.votesCast / st.votesCount) * 100).toFixed(1) : "n/a";
              const tiles = [
                { id: "stat-bills-sponsored", icon: TrendingUp, label: "Bills Sponsored", value: sponsored, sub: "Click to see list", onClick: () => openBillsDrilldown("sponsored"),
                  tip: cur ? "Substantive bills (excluding ceremonial resolutions) this member introduced in the 119th Congress, per Congress.gov." : "Total bills and resolutions this member has introduced over their career, per Congress.gov." },
                { id: "stat-bills-passed", icon: Award, label: "Bills Enacted", value: enacted, sub: "Click to see list", onClick: () => openBillsDrilldown("passed"),
                  tip: cur ? "Sponsored bills signed into law during the 119th Congress." : "Sponsored bills that became public law across the member's career." },
                { id: "stat-voting-record", icon: Vote, label: "Voting Record", value: `${votePct}%`, sub: cur ? "Roll calls cast ÷ eligible, 119th" : "Roll-call history available for the 119th Congress",
                  tip: "Share of recorded roll-call votes this member actually cast (Yea/Nay/Present) out of all roll calls held while they were seated, calculated from the WHIP roll-call cache." },
                { id: "hero-bipartisan-index", icon: UsersIcon, label: "Bipartisan Index", value: (st.bipartisanIndex ?? 0).toFixed(1), sub: "% of cross-party peers voting same way",
                  tip: "Of the opposite-party members who voted on the same roll calls, the percentage who voted the same way as this member. Higher = votes more often with the other side." },
              ];
              return tiles.map((t) => {
                const Icon = t.icon;
                const Tag = t.onClick ? "button" : "div";
                return (
                  <Tag key={t.id} type={t.onClick ? "button" : undefined} onClick={t.onClick} title={t.tip} data-testid={t.id}
                    className={`text-left bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/20 transition ${t.onClick ? "hover:bg-white/20 hover:border-white/40 cursor-pointer" : ""}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-5 h-5" />
                      <p className="text-sm font-medium opacity-90">{t.label}</p>
                      <span className="ml-auto text-[10px] opacity-60 font-mono">{cur ? "119th" : "Career"}</span>
                    </div>
                    <p className="text-3xl md:text-4xl font-bold">{t.value}</p>
                    <p className="text-[10px] opacity-70 mt-1">{t.sub}</p>
                  </Tag>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section tabs */}
        <div className="flex items-center gap-1 border-b border-white/10 mb-8 overflow-x-auto overflow-y-hidden whitespace-nowrap whip-no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="profile-tabs">
          {[
            { id: "overview", label: "Overview" },
            { id: "voting", label: "Voting" },
            { id: "legislation", label: "Legislation" },
            { id: "committees", label: "Committees" },
            { id: "news", label: "News" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] font-mono border-b-2 -mb-px whitespace-nowrap transition-colors ${
                activeTab === t.id
                  ? "border-orange-500 text-orange-300"
                  : "border-transparent text-white/45 hover:text-white/80"
              }`}
              data-testid={`profile-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" && (
          <OverviewTab legislator={legislator} legislatorId={legislatorId} loading={loading} committees={committees} lesScope={lesScope} setLesScope={setLesScope} lesHistory={lesHistory} />
        )}

        {activeTab === "voting" && (
          <VotingTab legislator={legislator} legislatorId={legislatorId} navigate={navigate} saveScroll={saveScroll} loading={loading} recentVotes={recentVotes} surpriseVotes={surpriseVotes} votingTribe={votingTribe} flipFlops={flipFlops} analyticsLoading={analyticsLoading} getVoteIcon={getVoteIcon} />
        )}

        {activeTab === "committees" && <CommitteesTab legislatorId={legislatorId} legislator={legislator} />}
        {activeTab === "news" && <NewsTab legislatorId={legislatorId} />}
        {activeTab === "legislation" && (
          <LegislationTab legislatorId={legislatorId} legislator={legislator} navigate={navigate} saveScroll={saveScroll} sponsoredBills={sponsoredBills} statsData={statsData} votingData={votingData} handlePieClick={handlePieClick} />
        )}
      </main>
      
      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{dialogData?.title}</DialogTitle>
            <DialogDescription className="text-lg font-semibold text-blue-600 mt-2">
              {dialogData?.value} {dialogData?.percentage && `(${dialogData.percentage})`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 leading-relaxed">{dialogData?.description}</p>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sponsored Bills Dialog */}
      <Dialog open={billsDialogOpen} onOpenChange={setBillsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{billsDialogData.title}</DialogTitle>
            <DialogDescription className="text-base text-white/50 mt-2" data-testid="bills-dialog-count">
              {billsDialogData.loading
                ? "Loading bills…"
                : billsDialogData.isAttendanceData
                ? "Complete attendance breakdown"
                : billsDialogData.kind === "sponsored" && billsDialogData.amendmentCount > 0
                ? `${billsDialogData.billCount} bills · ${billsDialogData.amendmentCount} ${billsDialogData.amendmentCount === 1 ? "amendment" : "amendments"}, ${billsDialogData.totalSponsored} total sponsored per Congress.gov`
                : billsDialogData.totalSponsored > billsDialogData.bills.length && billsDialogData.kind === "sponsored"
                ? `Showing ${billsDialogData.bills.length} most recent of ${billsDialogData.totalSponsored} sponsored`
                : `${billsDialogData.bills.length} bills`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {billsDialogData.loading ? (
              <div className="py-10 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-gray-600 text-sm">Fetching latest bills from Congress.gov…</p>
              </div>
            ) : billsDialogData.isAttendanceData && billsDialogData.attendanceData ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="text-left p-4 font-semibold text-gray-700">Category</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Count</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Percentage</th>
                      <th className="text-center p-4 font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billsDialogData.attendanceData.map((row) => (
                      <tr key={row.category} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-4 font-medium text-gray-900">{row.category}</td>
                        <td className="p-4 text-center text-gray-900">{row.count}</td>
                        <td className="p-4 text-center font-semibold text-gray-900">{row.percentage}</td>
                        <td className="p-4 text-center">
                          <Badge className={
                            row.status === "Present" ? "bg-green-100 text-green-800" :
                            row.status === "Absent" ? "bg-red-100 text-red-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {row.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : billsDialogData.bills.length > 0 ? (
              <div
                className="rounded-xl border border-white/10 overflow-hidden"
                style={{ background: "var(--whip-surface, #12121a)" }}
                data-testid="bills-dialog-list"
              >
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-white/60">
                    Newest First
                  </span>
                  <span className="text-[11px] font-mono text-white/35">CLICK A BILL FOR DETAILS</span>
                </div>
                {billsDialogData.bills.map((bill, i) => (
                  <div key={bill.id}>
                    <Link
                      to={`/bill/${bill.id}`}
                      state={{ fromLegislator: legislatorId }}
                      onClick={() => {
                        setBillsDialogOpen(false);
                        saveScroll();
                      }}
                      className="px-5 py-3.5 flex items-start gap-4 hover:bg-orange-500/[0.06] cursor-pointer transition-colors group"
                      data-testid={`bills-dialog-row-${i + 1}`}
                    >
                      <span className="text-2xl font-black font-mono text-white/15 leading-none pt-0.5 w-9 shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-xs font-mono font-bold text-orange-300">{bill.billNumber}</span>
                          <span className={statusPillClass(bill.status)}>{bill.status}</span>
                        </div>
                        <p className="text-white font-bold leading-snug group-hover:text-orange-200 transition-colors">
                          {bill.title}
                        </p>
                      </div>
                      <span className="text-[11px] font-mono text-white/40 shrink-0 pt-1">
                        {new Date(bill.introducedDate).toLocaleDateString()}
                      </span>
                    </Link>
                    {i < billsDialogData.bills.length - 1 && <div className="whip-divider" />}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/50 py-8 text-center">
                {billsDialogData.kind === "passed" ? "No enacted bills found" : "No sponsored bills found"}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegislatorProfile;
